/**
 * hooks/useBrokerSelection.ts
 * Hook for broker selection and filtering using light metadata
 * 
 * Provides fast broker discovery functionality using only essential
 * selection metadata. Replaces heavy useBrokerFiltering hook for
 * performance-critical broker selection workflows.
 */

import { useMemo } from 'react';
import type { BrokerCatalogEntry } from '@/types/reference/BrokerCatalog';
import type { AssetClassType } from '@/types/shared/AssetClass';
import type { AccountType } from '@/types/shared/AccountType';
import { 
  BROKER_CATALOG_DATA,
  getPopularBrokers
} from '@/data/brokerCatalog';

/**
 * Enhanced broker filtering options for selection phase
 */
export interface BrokerSelectionFilterOptions {
  /** Search query to filter by broker name */
  searchQuery?: string;
  
  /** Account type filter - personal vs prop firm brokers */
  accountType?: AccountType;
  
  /** Asset classes that must be supported */
  requiredAssetClasses?: AssetClassType[];
  
  /** Prop firm that broker must support (prop accounts) */
  propFirmId?: string;
  
  /** Only show popular brokers */
  popularOnly?: boolean;
  
  /** Show all brokers regardless of other filters (override) */
  showAllBrokers?: boolean;
  
  /** Custom broker filter function */
  customFilter?: (broker: BrokerCatalogEntry) => boolean;
}

/**
 * Filtered broker selection results with categorization
 */
export interface BrokerSelectionResults {
  /** Brokers that perfectly match all criteria */
  recommended: BrokerCatalogEntry[];
  
  /** All brokers (including partial matches) */
  all: BrokerCatalogEntry[];
  
  /** Total count of available brokers */
  totalCount: number;
  
  /** Count of brokers matching search query */
  searchResultCount: number;
  
  /** Whether any filters are applied */
  hasFilters: boolean;
  
  /** Applied filter summary for debugging */
  appliedFilters: {
    searchQuery: string | undefined;
    accountType: AccountType | undefined;
    requiredAssetClasses: AssetClassType[] | undefined;
    propFirmId: string | undefined;
    popularOnly: boolean | undefined;
  };
}

/**
 * Hook for fast broker selection using light metadata
 */
export const useBrokerSelection = (
  options: BrokerSelectionFilterOptions = {}
): BrokerSelectionResults => {
  const {
    searchQuery = '',
    accountType,
    requiredAssetClasses = [],
    propFirmId,
    popularOnly = false,
    showAllBrokers = false,
    customFilter
  } = options;

  const filteredResults = useMemo(() => {
    let allBrokers = BROKER_CATALOG_DATA;

    // Apply custom filter first if provided
    if (customFilter) {
      allBrokers = allBrokers.filter(customFilter);
    }

    // CLEAN 3-CASE FILTERING APPROACH:
    // Step 1: Build the recommended list using clear business logic
    let recommendedBrokers: BrokerCatalogEntry[] = [];
    
    if (accountType === 'paper') {
      // Paper accounts: show only paper trading brokers
      recommendedBrokers = allBrokers.filter(broker => broker.accountType === 'paper');
      
    } else if (accountType === 'prop') {
      // Prop accounts: show only prop brokers that support the selected prop firm
      recommendedBrokers = allBrokers.filter(broker => 
        broker.accountType === 'prop' && 
        (!propFirmId || (broker.supportedPropFirms?.includes(propFirmId) ?? false))
      );
      
    } else {
      // All other accounts (personal, retirement, business, trust, pooled, other):
      // Show general purpose brokers with asset class filtering
      recommendedBrokers = allBrokers.filter(broker => {
        // Must be a general purpose broker (has array account type)
        if (!Array.isArray(broker.accountType)) {
          return false;
        }
        
        // Must support the specific account type
        if (accountType && !broker.accountType.includes(accountType)) {
          return false;
        }
        
        // Must support all required asset classes
        if (requiredAssetClasses.length > 0) {
          const hasAllAssets = requiredAssetClasses.every(assetClass => 
            broker.assetClasses.includes(assetClass)
          );
          if (!hasAllAssets) {
            return false;
          }
        }
        
        return true;
      });
    }
    
    // Apply popularity filter if requested (across all account types)
    if (popularOnly) {
      recommendedBrokers = recommendedBrokers.filter(broker => broker.isPopular);
    }
    
    // Step 2: Build the "all" list
    // When showAllBrokers is true, show everything
    // When false, we still need to return something for the UI to work, but it won't be displayed
    let allBrokersList;
    if (showAllBrokers) {
      // Show everything from catalog when toggle is ON
      allBrokersList = allBrokers;
    } else {
      // When toggle is OFF, the UI won't show this section anyway
      // But we still need to return the filtered list for consistency
      allBrokersList = recommendedBrokers;
    }
    
    // Step 3: Apply search filter to both sets
    const searchFilteredRecommended = searchQuery
      ? recommendedBrokers.filter(broker =>
          broker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          broker.shortName?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : recommendedBrokers;

    const searchFilteredAll = searchQuery
      ? allBrokersList.filter(broker =>
          broker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          broker.shortName?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : allBrokersList;

    return {
      recommended: searchFilteredRecommended,
      all: searchFilteredAll,
      totalCount: allBrokers.length, // Always use the full catalog size, not the filtered list
      searchResultCount: searchFilteredAll.length,
      hasFilters: Boolean(searchQuery || requiredAssetClasses.length > 0 || propFirmId || popularOnly || accountType),
      appliedFilters: {
        searchQuery: searchQuery || undefined,
        accountType,
        requiredAssetClasses: requiredAssetClasses.length > 0 ? requiredAssetClasses : undefined,
        propFirmId,
        popularOnly: popularOnly || undefined
      }
    };
  }, [searchQuery, accountType, requiredAssetClasses, propFirmId, popularOnly, showAllBrokers, customFilter]);

  return filteredResults;
};

/**
 * Helper hook for prop firm broker selection (simplified interface)
 */
export const usePropFirmBrokerSelection = (
  propFirmId: string,
  searchQuery: string = ''
) => {
  return useBrokerSelection({
    searchQuery,
    accountType: 'prop',
    propFirmId
  });
};

/**
 * Helper hook for general account broker selection (simplified interface)
 * Supports all general account types: personal, retirement, business, trust, pooled, other
 */
export const useGeneralAccountBrokerSelection = (
  accountType: 'personal' | 'retirement' | 'business' | 'trust' | 'pooled' | 'other',
  requiredAssetClasses: AssetClassType[],
  searchQuery: string = ''
) => {
  return useBrokerSelection({
    searchQuery,
    accountType,
    requiredAssetClasses
  });
};

/**
 * @deprecated Use useGeneralAccountBrokerSelection instead
 * Helper hook for personal account broker selection (simplified interface)
 */
export const usePersonalAccountBrokerSelection = (
  requiredAssetClasses: AssetClassType[],
  searchQuery: string = ''
) => {
  return useGeneralAccountBrokerSelection('personal', requiredAssetClasses, searchQuery);
};

/**
 * Helper hook to get broker by ID
 */
export const useBrokerById = (brokerId: string): BrokerCatalogEntry | null => {
  return useMemo(() => {
    return BROKER_CATALOG_DATA.find(broker => broker.id === brokerId) || null;
  }, [brokerId]);
};

/**
 * Helper hook to get popular brokers by account type
 */
export const usePopularBrokers = (accountType?: AccountType) => {
  return useMemo(() => {
    let brokers = getPopularBrokers();
    if (accountType) {
      brokers = brokers.filter(broker => {
        // Handle array account types (general purpose brokers)
        if (Array.isArray(broker.accountType)) {
          return broker.accountType.includes(accountType);
        }
        // Handle single account type (prop/paper brokers)
        return broker.accountType === accountType;
      });
    }
    return brokers;
  }, [accountType]);
};